import {
  type DefinedQueryObserverResult,
  QueryCache,
  QueryClient,
  type QueryKey,
  useQuery as useReactQuery,
  type UseQueryOptions,
} from "@tanstack/react-query";

import { fetchVisitorSession } from "api/visitor-session";

import { getSlugFromPathname } from "./getSlugFromPathname";
import { VisitorNotFoundException } from "./http";

async function resetVisitorSession() {
  const slug = getSlugFromPathname();
  if (!slug) return;

  indexedDB.deleteDatabase("keyval-store");
  await fetchVisitorSession(slug);
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      if (error instanceof VisitorNotFoundException) {
        void resetVisitorSession();
      }
    },
  }),
});

export function useQuery<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey
>(options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>) {
  // Merge query-level defaults (e.g. staleTime: Infinity from SSR seeding)
  // with higher priority than our fallback staleTime.
  const defaults = options.queryKey
    ? queryClient.getQueryDefaults(options.queryKey) ?? {}
    : {};

  // If data was seeded by SSR (staleTime: Infinity in defaults), wrap
  // queryFn to return cached data instead of making a network call.
  // This is a safety net: even if RQ v4 decides to call queryFn despite
  // the data being in cache, the function returns the seeded data.
  let wrappedOptions = options;
  if (defaults.staleTime === Infinity && options.queryFn && options.queryKey) {
    const originalFn = options.queryFn;
    const key = options.queryKey;
    wrappedOptions = {
      ...options,
      queryFn: (ctx) => {
        const cached = queryClient.getQueryData<TQueryFnData>(key);
        if (cached !== undefined) {
          return cached;
        }
        return typeof originalFn === "function" ? originalFn(ctx) : originalFn;
      },
    };
  }

  return useReactQuery<TQueryFnData, TError, TData, TQueryKey>({
    staleTime: 2000,
    ...defaults,
    ...wrappedOptions,
  }) as DefinedQueryObserverResult<TData, TError>;
}
